/**
 * Privacy Policy Page
 *
 * GDPR-compliant privacy policy for Have-Done.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Eye, Trash2, Download, Mail } from 'lucide-react';
import { Footer } from './Footer';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-bg-primary flex flex-col">
      <div className="mx-auto max-w-3xl w-full px-3 sm:px-4 py-8 flex-1">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to app
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-text-primary">
              Privacy Policy
            </h1>
            <p className="text-sm text-text-secondary">
              Last updated: January 2025 | Version 1.0.0
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <p className="text-text-secondary leading-relaxed">
              Have-Done ("we", "our", or "us") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, and safeguard your personal
              information when you use our productivity application.
            </p>
            <p className="text-text-secondary leading-relaxed">
              <strong className="text-text-primary">Our philosophy:</strong> Your achievements are personal.
              We believe in collecting only what's necessary and giving you full control over your data.
            </p>
          </section>

          {/* What we collect */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold text-text-primary m-0">
                What We Collect
              </h2>
            </div>
            <div className="bg-bg-secondary rounded-lg p-4 space-y-4">
              <div>
                <h3 className="font-medium text-text-primary mb-2">Account Information</h3>
                <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                  <li>Email address (from Google/Apple sign-in)</li>
                  <li>Display name (optional)</li>
                  <li>Profile picture URL (from sign-in provider)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-text-primary mb-2">Your Entries</h3>
                <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                  <li>Achievement descriptions you write</li>
                  <li>Categories you assign</li>
                  <li>Satisfaction ratings (if provided)</li>
                  <li>Time spent (if provided)</li>
                  <li>Dates and timestamps</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-text-primary mb-2">Analytics (with consent)</h3>
                <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                  <li>Page views and feature usage</li>
                  <li>Device type and browser</li>
                  <li>General geographic region</li>
                </ul>
                <p className="text-xs text-text-tertiary mt-2">
                  Analytics are only collected if you consent. You can change this anytime in settings.
                </p>
              </div>
            </div>
          </section>

          {/* How we use it */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold text-text-primary m-0">
                How We Use Your Data
              </h2>
            </div>
            <ul className="text-text-secondary space-y-2">
              <li className="flex gap-2">
                <span className="text-success">&#x2713;</span>
                <span>Provide and improve the Have-Done service</span>
              </li>
              <li className="flex gap-2">
                <span className="text-success">&#x2713;</span>
                <span>Sync your entries across devices</span>
              </li>
              <li className="flex gap-2">
                <span className="text-success">&#x2713;</span>
                <span>Generate your personal statistics and summaries</span>
              </li>
              <li className="flex gap-2">
                <span className="text-success">&#x2713;</span>
                <span>Send important account notifications</span>
              </li>
              <li className="flex gap-2">
                <span className="text-error">&#x2717;</span>
                <span className="line-through">Sell your data to third parties</span>
              </li>
              <li className="flex gap-2">
                <span className="text-error">&#x2717;</span>
                <span className="line-through">Show you targeted advertisements</span>
              </li>
              <li className="flex gap-2">
                <span className="text-error">&#x2717;</span>
                <span className="line-through">Share your entries with anyone</span>
              </li>
            </ul>
          </section>

          {/* Your rights */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              Your Rights (GDPR)
            </h2>
            <p className="text-text-secondary mb-4">
              Under GDPR and similar regulations, you have the following rights:
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="bg-bg-secondary rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Download className="w-4 h-4 text-primary" />
                  <h3 className="font-medium text-text-primary">Right to Access</h3>
                </div>
                <p className="text-sm text-text-secondary">
                  Request a copy of all your personal data at any time.
                </p>
              </div>
              <div className="bg-bg-secondary rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Trash2 className="w-4 h-4 text-primary" />
                  <h3 className="font-medium text-text-primary">Right to Erasure</h3>
                </div>
                <p className="text-sm text-text-secondary">
                  Delete your account and all associated data permanently.
                </p>
              </div>
              <div className="bg-bg-secondary rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-primary" />
                  <h3 className="font-medium text-text-primary">Right to Portability</h3>
                </div>
                <p className="text-sm text-text-secondary">
                  Export your data in a machine-readable format (JSON).
                </p>
              </div>
              <div className="bg-bg-secondary rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-primary" />
                  <h3 className="font-medium text-text-primary">Right to Restrict</h3>
                </div>
                <p className="text-sm text-text-secondary">
                  Opt out of analytics while keeping your account.
                </p>
              </div>
            </div>
            <p className="text-sm text-text-secondary mt-4">
              Exercise these rights in{' '}
              <Link to="/settings" className="text-primary hover:underline">
                Settings &gt; Privacy
              </Link>
              .
            </p>
          </section>

          {/* Data storage */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              Data Storage & Security
            </h2>
            <ul className="text-text-secondary space-y-2">
              <li>
                <strong className="text-text-primary">Backend:</strong> Convex (cloud database with encryption at rest)
              </li>
              <li>
                <strong className="text-text-primary">Analytics:</strong> PostHog (EU data center, GDPR compliant)
              </li>
              <li>
                <strong className="text-text-primary">Authentication:</strong> OAuth 2.0 via Google/Apple (we never see your password)
              </li>
              <li>
                <strong className="text-text-primary">Payments:</strong> Mollie (PCI DSS compliant)
              </li>
            </ul>
          </section>

          {/* Data retention */}
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              Data Retention
            </h2>
            <ul className="text-text-secondary space-y-2">
              <li>
                <strong className="text-text-primary">Your entries:</strong> Kept until you delete them or your account
              </li>
              <li>
                <strong className="text-text-primary">Analytics:</strong> Automatically deleted after 90 days
              </li>
              <li>
                <strong className="text-text-primary">Audit logs:</strong> Retained for 5 years for legal compliance
              </li>
              <li>
                <strong className="text-text-primary">Account deletion:</strong> 30-day grace period, then permanent deletion
              </li>
            </ul>
          </section>

          {/* Contact */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold text-text-primary m-0">
                Contact Us
              </h2>
            </div>
            <p className="text-text-secondary">
              For privacy-related questions or to exercise your rights:
            </p>
            <p className="text-text-secondary mt-2">
              <strong className="text-text-primary">Email:</strong>{' '}
              <a href="mailto:privacy@have-done.app" className="text-primary hover:underline">
                privacy@have-done.app
              </a>
            </p>
          </section>

          {/* Changes */}
          <section className="border-t border-border-light pt-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              Changes to This Policy
            </h2>
            <p className="text-text-secondary">
              We may update this policy from time to time. If we make significant changes,
              we'll notify you via email or in-app notification. Your continued use of
              Have-Done after changes constitutes acceptance of the updated policy.
            </p>
          </section>
        </div>

      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
